import type { JSX } from 'react';
import { Images } from 'lucide-react';
import { Button } from '../ui/button';
import { useImageGallery } from '../../contexts/ImageGalleryContext';
import { useI18n } from '../../i18n';

/**
 * The gallery icon (group Q): on the table's sidebar rail next to the settings gear, and in the Подготовка top bar.
 * Label in the tooltip (the rail rule of the sidebar mockup: icon + data-tip/aria-label). While MY pictures are being
 * generated it pulses and shows their number.
 */
export function ImageGalleryButton({ placement }: { placement: 'rail' | 'topbar' }): JSX.Element | null {
  const g = useImageGallery();
  const { t } = useI18n();
  if (!g) return null;
  const busy = g.myActive > 0;
  const label = busy ? t('images.titleBusy').replace('{n}', String(g.myActive)) : t('images.title');
  return (
    <Button
      type="button"
      variant="ghost"
      size={placement === 'rail' ? 'inline' : 'icon'}
      className={placement === 'rail'
        ? `sidebar-icon-strip__item [&_svg]:size-4.5${g.open ? ' sidebar-icon-strip__item--active' : ''}`
        : `relative text-muted-foreground hover:text-fg [&_svg]:size-4${g.open ? ' text-hl' : ''}`}
      onClick={g.toggleGallery}
      title={label}
      aria-label={label}
      aria-pressed={g.open}
      data-testid="images-gallery-button"
      data-busy={busy ? 'true' : 'false'}
    >
      <span className={placement === 'rail' ? 'sidebar-icon-strip__icon' : 'flex'}>
        <Images size={16} className={busy ? 'animate-pulse text-hl' : undefined} aria-hidden="true" />
      </span>
      {busy ? (
        <span
          className="absolute right-0.5 top-0.5 min-w-4 rounded-full bg-hl px-1 text-center text-label font-semibold leading-4 text-hl-ink"
          data-testid="images-gallery-count"
        >
          {g.myActive}
        </span>
      ) : null}
    </Button>
  );
}
